// ArtistFilter.tsx
import React, { useMemo, useState } from "react";
import type { Artist } from "../../models/artist";
import './ArtistFilterStyleSheet.css';

interface ArtistFilterProps {
    artists: Artist[];
    selectedIds?: number[];
    onChange: (ids: number[]) => void;
}

export const ArtistFilter: React.FC<ArtistFilterProps> = ({
    artists,
    selectedIds = [],
    onChange,
}) => {
    const [searchText, setSearchText] = useState("");

    const filteredArtists = useMemo(() => {
        const term = searchText.trim().toLowerCase();
        if (!term) return artists;

        return artists.filter((a) =>
            a.artistName.toLowerCase().includes(term) ||
            (a.countryName?.toLowerCase().includes(term) ?? false) ||
            (a.artistPeriod?.toLowerCase().includes(term) ?? false) ||
            (a.biography?.toLowerCase().includes(term) ?? false) ||
            (a.clientBio?.toLowerCase().includes(term) ?? false) ||
            (a.nationality?.toLowerCase().includes(term) ?? false) 
        );
    }, [artists, searchText]);

    const toggleArtist = (id: number) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter((x) => x !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    const clearAll = () => onChange([]);

    return (
        <div className="filter-section">
            <div className="filter-section-header">
                {selectedIds.length > 0 && (
                    <>
                        <p className="filter-count">Selected {selectedIds.length} artists</p>
                        <button
                            type="button"
                            className="filter-clear-btn"
                            onClick={clearAll}
                        >
                        Clear
                        </button>
                    </>
                )}
            </div>

            <div className="filter-search-input-wrapper">
                <input
                    type="text"
                    className="filter-search-input"
                    placeholder="Search artists…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
            </div>

            <div className="filter-list">
                {filteredArtists.length === 0 && (
                    <div className="filter-empty">No artists match your search.</div>
                )}

                {filteredArtists.map((artist) => {
                    const checked = selectedIds.includes(artist.artistId);
                    return (
                        <label
                            key={artist.artistId}
                            className={`filter-list-item ${checked ? "filter-list-item--selected" : ""
                                }`}
                        >
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleArtist(artist.artistId)}
                            />
                            <span className="filter-list-label">
                                {artist.artistName}
                                {artist.countryName && (
                                    <span className="filter-list-sub">
                                        {" "}
                                        · {artist.countryName}
                                    </span>
                                )}
                            </span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
};
